import Axios, { AxiosError, AxiosInstance } from "axios";
import { AxiosCacheInstance, setupCache } from "axios-cache-interceptor";

import { EnvVar } from "@/env";
import { tenantIdStorage } from "@/storage";

export const BASE_URL = EnvVar.backendUrl;
export const INSIGHTS_BACKEND_URL = EnvVar.insightsBackendUrl;
export const NEST_BACKEND_URL = EnvVar.nestBackendUrl;

export const DefaultCachePeriodMs = 60 * 60 * 1000;

export const axiosApi = setupCache(
  Axios.create({
    baseURL: INSIGHTS_BACKEND_URL,
  }),
  {
    methods: ["get", "post"],
    ttl: DefaultCachePeriodMs,
    cachePredicate: {
      ignoreUrls: [
        RegExp("^/api/v2/scenarios-new"),
        RegExp("^/api/v2/nodes"),
        RegExp("^/api/v2/roi"),
        RegExp("^/api/v2/channels"),
        RegExp("^/api/v2/investment-levels"),
        RegExp("^/api/v2/campaigns"),
        RegExp("^/api/v2/flighting"),
        RegExp("^/api/v2/financial-review"),
        RegExp("^/api/v2/settings/theme"),
      ],
    },
  }
);

axiosApi.interceptors.request.use(
  (config) => {
    config.headers["x-tenant-id"] = tenantIdStorage.get();
    return config;
  },
  (error) => {
    console.error(error);
    return Promise.reject(error);
  }
);

export const withAxiosApiResponseInterceptors = (instance: AxiosInstance | AxiosCacheInstance) => {
  instance.interceptors.response.use(
    (response) => {
      if (
        response.config.url &&
        (response.config.url.startsWith("/api/scenarios-new") ||
          response.config.url.startsWith("/api/nodes") ||
          response.config.url.startsWith("/api/roi") ||
          response.config.url.startsWith("/api/channels") ||
          response.config.url.startsWith("/api/investment-levels") ||
          response.config.url.startsWith("/api/campaigns") ||
          response.config.url.startsWith("/api/flighting") ||
          response.config.url.startsWith("/api/formats") ||
          response.config.url.startsWith("/api/publishers") ||
          response.config.url.startsWith("/api/geographies") ||
          response.config.url.startsWith("/api/financial-review") ||
          response.config.url.startsWith("/api/sales-drivers") ||
          response.config.url.startsWith("/api/info") ||
          response.config.url.startsWith("/api/v1/info") ||
          response.config.url.startsWith("/api/roi/shared") ||
          response.config.url.startsWith("/api/team/users") ||
          // response.config.url.startsWith("/api/v1/flighting") ||
          // response.config.url.startsWith("/api/v1/geography") ||
          // response.config.url.startsWith("/api/v1/campaign") ||
          // response.config.url.startsWith("/api/v1/format") ||
          // response.config.url.startsWith("/api/v1/publisher") ||
          // response.config.url.startsWith("/api/v1/info/min_max_range") ||
          // response.config.url.startsWith("/api/v1/info/geographies") ||
          // response.config.url.startsWith("/api/v1/investment_levels") ||
          // v2 routes, served by insights-service
          response.config.url.startsWith("/api/v2/team/users") ||
          response.config.url.startsWith("/api/v2/info") ||
          response.config.url.startsWith("/api/v2/info/v2") ||
          response.config.url.startsWith("/api/v2/scenarios-new") ||
          response.config.url.startsWith("/api/v2/nodes") ||
          response.config.url.startsWith("/api/v2/roi") ||
          response.config.url.startsWith("/api/v2/channels") ||
          response.config.url.startsWith("/api/v2/investment-levels") ||
          response.config.url.startsWith("/api/v2/campaigns") ||
          response.config.url.startsWith("/api/v2/flighting") ||
          response.config.url.startsWith("/api/v2/formats") ||
          response.config.url.startsWith("/api/v2/publishers") ||
          response.config.url.startsWith("/api/v2/geographies") ||
          response.config.url.startsWith("/api/v2/financial-review") ||
          response.config.url.startsWith("/api/v2/sales-drivers"))
      ) {
        return response.data;
      }
      return response;
    },
    (error) => {
      // Log error
      console.error(error);
      return Promise.reject(error);
    }
  );
};

export const isUnauthorizedError = (error: unknown): error is AxiosError => {
  if (Axios.isAxiosError(error)) {
    return error.response?.status === 401 || error.response?.status === 403;
  }

  return false;
};

withAxiosApiResponseInterceptors(axiosApi);
